import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useOfferDetailsContext } from '@frontend/context/OfferDetailsContext';
import { HouseIcon } from '@revfluence/fresh-icons/solid/esm';
import HeaderButton from '@frontend/app/refresh-components/HeaderButton';
import NeedHelpDropdown from '../../../OfferForm/utils/NeedHelpDropdown';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shadcn/components/ui/breadcrumb';

interface OfferSettingsCustomTitleProps {
  onBack?: () => void;
}

export const OfferSettingsCustomTitle: React.FC<OfferSettingsCustomTitleProps> = ({
  onBack,
}) => {
  const history = useHistory();
  const location = useLocation();
  const { offerFormValue } = useOfferDetailsContext();

  const offerName = offerFormValue?.name || 'Ambassador Offer';

  // Get current tab from URL or default to 'Automations'
  const getCurrentTab = () => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get('tab');

    switch (tab) {
      case 'offer-settings': return 'Offer Settings';
      case 'email-template': return 'Email Template';
      case 'automation': return 'Automations';
      case 'run-history': return 'Run History';
      default: return 'Automations';
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      history.goBack();
    }
  };

  return (
    <div className="w-full">
      {/* Top row with breadcrumb and Need Help */}
      <div className="flex items-center justify-between w-full mb-2">
        <Breadcrumb>
          <BreadcrumbList className="text-xs gap-1">
            <BreadcrumbItem>
              <BreadcrumbLink
                href="#"
                className=" transition-colors text-grey-4"
              >
                <HouseIcon className="h-4 w-4 text-grey-4" />
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-grey-4 [&>svg]:w-3 [&>svg]:h-3" />
            <BreadcrumbItem>
              <BreadcrumbLink
                href="#"
                className="text-xs text-grey-4"
              >
                Sales Tracking
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-grey-4 [&>svg]:w-3 [&>svg]:h-3" />
            <BreadcrumbItem>
              <BreadcrumbLink
                href="#"
                className="text-xs text-grey-4"
              >
                {offerName}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-grey-4 [&>svg]:w-3 [&>svg]:h-3" />
            <BreadcrumbItem>
              <BreadcrumbLink
                href="#"
                className="text-xs text-grey-4"
              >
                Edit Offer
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-grey-4 [&>svg]:w-3 [&>svg]:h-3" />
            <BreadcrumbItem>
              <BreadcrumbPage className="text-grey-0 text-xs font-medium">
                {getCurrentTab()}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Bottom row with back button and offer name */}
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center space-x-3">
          <HeaderButton
            onClick={handleBack}
            icon={<ArrowLeft className="h-4 w-4" />}
          />
          <h1 className="text-grey-0 font-medium text-lg m-0 leading-none">
            {offerName}
          </h1>
        </div>
        <div className="flex items-center">
          <NeedHelpDropdown key={1} source={offerFormValue?.source} />
        </div>
      </div>
    </div>
  );
};
